import type { SearchOptions } from './search';

/** The four standards of board opponent; the arena skill uses the same four words. */
export type BoardLevel = 'novice' | 'fair' | 'expert' | 'master';
export const BOARD_LEVELS: readonly BoardLevel[] = ['novice', 'fair', 'expert', 'master'];

/** What a level puts into the search; `boardAiOptions` turns it into options. */
export interface BoardAiProfile {
  /** Plies searched. */
  depth: number;
  /** Replies kept per inner node, by ply from the root (`SearchOptions.beams`). */
  beams: readonly number[];
  /** Root moves that get the full depth from three plies on (`SearchOptions.fullWidth`). */
  fullWidth?: number;
  /** How much threats are feared, 0 … 1. */
  caution: number;
  /** How much of each choice is chance, 0 … 1 (`SearchOptions.randomness`). */
  randomness: number;
  /** Seeded variety among near-best moves in the opening (`SearchOptions.openingVariety`). */
  openingVariety?: { rounds: number; margin: number };
  /** Look through the recapture after a fight at the horizon before judging a position. */
  extendCaptures: boolean;
  /** Judge a position from the side to move (`AiOptions.initiative`); needed for an even depth. */
  initiative: boolean;
}

/**
 * How the levels without chance open a game (D-55): each side's first four moves are chosen among
 * those within two evaluation units of the best (a sixth of a power point), by the game's seed.
 * In practice they pick among moves the search finds exactly as good, where the move ordering used
 * to pick the same one every game, and so every game opened alike.
 */
export const OPENING_VARIETY = { rounds: 4, margin: 2 } as const;

/**
 * Every level decides from the same odds and evaluation; what changes is how far it looks and
 * how much of the choice is chance. Novice looks one move ahead, fears little and plays half by
 * chance, so it takes fair moves over the best one and never plays the same game twice. Fair sees
 * the reply and plays a quarter by chance. Expert is the reference every balance figure is
 * measured with (`docs/09-balance.md`): three plies, the search's own beam and caution, no chance
 * but its opening variety. Master searches exactly as Expert does and then, within the clock its
 * caller gives it, adds a fourth ply: full width on the eight most promising moves, a narrow beam
 * on that last ply so it stays affordable, and a look through the recapture after every fight at
 * the horizon. When the clock stops it first, Master plays Expert's move, never a lesser one.
 */
export const BOARD_AI_PROFILES: Readonly<Record<BoardLevel, BoardAiProfile>> = {
  novice: {
    depth: 1,
    beams: [24],
    caution: 0.4,
    randomness: 0.5,
    extendCaptures: false,
    initiative: false,
  },
  fair: {
    depth: 2,
    beams: [24],
    caution: 0.35,
    randomness: 0.25,
    extendCaptures: false,
    initiative: true,
  },
  expert: {
    depth: 3,
    beams: [24],
    caution: 0.35,
    randomness: 0,
    openingVariety: OPENING_VARIETY,
    extendCaptures: false,
    initiative: false,
  },
  master: {
    depth: 4,
    beams: [24, 24, 24, 6],
    fullWidth: 8,
    caution: 0.35,
    randomness: 0,
    openingVariety: OPENING_VARIETY,
    extendCaptures: true,
    initiative: true,
  },
};

/** The level a seat's depth setting stands for: 1 Novice, 2 Fair, 3 Expert, 4 and up Master. */
export function boardLevelOf(depth: number): BoardLevel {
  const index = Math.max(0, Math.min(BOARD_LEVELS.length - 1, Math.round(depth) - 1));
  return BOARD_LEVELS[index]!;
}

/** Search options for a seat's depth setting; the caller adds its clock. */
export function boardAiOptions(depth: number): SearchOptions {
  const profile = BOARD_AI_PROFILES[boardLevelOf(depth)];
  return {
    depth: profile.depth,
    beams: profile.beams,
    fullWidth: profile.fullWidth,
    caution: profile.caution,
    randomness: profile.randomness,
    openingVariety: profile.openingVariety,
    extendCaptures: profile.extendCaptures,
    initiative: profile.initiative,
  };
}

/**
 * The board AI's thinking time (D-36): a move takes three to four seconds as a rule, up to eight
 * in exceptional cases, and never more. A level's own plies, Expert's three and fewer, may use
 * the whole limit; a ply beyond them, Master's fourth, is started only when it should be done
 * within the target.
 */
export const THINK_LIMIT_MS = 8000;
export const THINK_TARGET_MS = 4000;
/** The plies every level at Expert and above counts as its own; deeper ones are extra. */
const OWN_PLIES = 3;

/** The clock for a seat's depth setting (D-36), for a caller that can give the search one. */
export function boardAiClock(
  depth: number,
): Required<Pick<SearchOptions, 'timeLimitMs' | 'softLimitMs' | 'minDepth'>> {
  return {
    timeLimitMs: THINK_LIMIT_MS,
    softLimitMs: THINK_TARGET_MS,
    minDepth: Math.min(OWN_PLIES, BOARD_AI_PROFILES[boardLevelOf(depth)].depth),
  };
}
